// Shared URL-driven search+pagination state for a filtered list page —
// debounced free-text `q` synced to the URL, a clamped `page`, and generic
// filter setters. `HrCases.tsx`, `HrMembers.tsx`, and `ManagerDashboard.tsx`
// each independently hand-rolled this same debounce/URL-resync dance; this
// is the first consumer to use the extracted version (the other two are
// pre-existing and out of scope for the pass that added this hook — a
// natural follow-up is retrofitting them).

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useDebouncedValue } from './use-debounced-value.ts';

export function useListSearchParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const qParam = searchParams.get('q') ?? '';
  const rawPage = Number(searchParams.get('page') ?? '0');
  // Clamp a malformed/negative `page` (hand-edited URL) to a valid integer
  // rather than propagating NaN/negative offsets into the API request.
  const page = Number.isFinite(rawPage) && rawPage >= 0 ? Math.trunc(rawPage) : 0;

  const [qInput, setQInput] = useState(qParam);
  const debouncedQ = useDebouncedValue(qInput, 300);
  // Tracks the last value THIS hook pushed into the URL, so the resync
  // effect below can tell "the URL changed because we just typed" (no-op)
  // apart from "the URL changed from elsewhere" (browser back/forward,
  // another setSearchParams caller) — which must resync qInput, or it
  // silently disagrees with the URL and the results.
  const lastPushedQ = useRef(qParam);

  useEffect(() => {
    if (debouncedQ === qParam) return;
    lastPushedQ.current = debouncedQ;
    const sp = new URLSearchParams(searchParams);
    if (debouncedQ) sp.set('q', debouncedQ);
    else sp.delete('q');
    sp.delete('page');
    setSearchParams(sp, { replace: true });
    // Deliberately narrow deps: only react to the debounced value settling.
    // Including searchParams/qParam would loop against our own
    // setSearchParams call above.
  }, [debouncedQ]);

  useEffect(() => {
    if (qParam === lastPushedQ.current) return;
    lastPushedQ.current = qParam;
    setQInput(qParam);
  }, [qParam]);

  // Sets (or clears, on an empty value) an arbitrary filter param and resets
  // to page 0 — for caller-specific filters (status, managerId, role, ...).
  function setParam(key: string, value: string) {
    const sp = new URLSearchParams(searchParams);
    if (!value) sp.delete(key);
    else sp.set(key, value);
    sp.delete('page');
    setSearchParams(sp, { replace: true });
  }

  function setPage(next: number) {
    const sp = new URLSearchParams(searchParams);
    if (next === 0) sp.delete('page');
    else sp.set('page', String(next));
    setSearchParams(sp, { replace: true });
  }

  return { searchParams, qParam, qInput, setQInput, page, setParam, setPage };
}
